import React, { useState } from 'react';
import { AccountPageLayout } from 'pages/Account/components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Select,
  Text,
  TextField,
} from '@clariness/clarikit-react';
import { themes } from '@clariness/clarikit-tokens';
import { Store } from 'store';
import { useMultiStep } from 'components/MultiStep';
import * as _ from 'lodash-es';
import { SiteEmployeeTitle } from 'global/siteEmployeeTitle';
import { Services } from 'services';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Events } from '@clariness/tracking-event-runtime';

export const AccountDetailsForm = () => {
  const intl = useIntl();
  const { space } = themes.light;

  const { nextStep } = useMultiStep();
  const { user } = Store.useUser();

  const [formValues, setFormValues] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [title, setTitle] = useState({
    label: user?.title ? SiteEmployeeTitle[user.title].label : '',
    value: user?.title,
  });

  const { updateCurrentSiteEmployee, isLoading } =
    Services.SiteEmployees.useUpdateCurrentSiteEmployee({
      onSuccess: (data) => {
        captureEventV2(
          Events.InvestigatorService.InvestigatorChangeAccountDetails({
            InvestigatorID: user?.id ?? '',
          })
        );
        setFormValues({
          ...formValues,
          ...data,
        });
      },
    });

  const validate = () => {
    if (formValues.firstName && formValues.lastName) {
      return true;
    }
    return false;
  };

  const handleChange = (value: string, name: string) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (): void => {
    if (validate()) {
      updateCurrentSiteEmployee({ ...formValues, title: title.value });
    }
  };

  return (
    <AccountPageLayout>
      <AccountPageLayout.Heading>
        <FormattedMessage
          id="is.account_details.heading"
          defaultMessage="Account details"
        />
      </AccountPageLayout.Heading>

      <AccountPageLayout.Paragraph>
        <FormattedMessage
          id="is.account_details.main_paragraph"
          defaultMessage="You can update your personal data and change your password here."
        />
      </AccountPageLayout.Paragraph>

      <AccountPageLayout.Form onSubmit={handleSubmit}>
        <Box
          display="grid"
          style={{
            columnGap: '54px',
            rowGap: '34px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}
        >
          <Select
            id="account-title-select"
            defaultSelectedValue={
              title.value
                ? { id: title.value, label: title.label, value: title.value }
                : null
            }
            onChange={(option) => {
              setTitle({
                label: option?.label ?? '',
                value: option?.value as any,
              });
            }}
            placeholder={intl.formatMessage({
              id: 'is.account_details.title_select_placeholder',
              defaultMessage: 'Title',
            })}
            data-test-id="account-title-select"
            dropDownOptionList={_.map(
              SiteEmployeeTitle,
              (siteEmployeeTitle) => ({
                id: siteEmployeeTitle.value,
                label: siteEmployeeTitle.label,
                value: siteEmployeeTitle.value,
              })
            )}
          />

          <TextField
            label="First name"
            requiredIndicator={true}
            labelHidden
            name="firstName"
            value={formValues.firstName}
            onChange={(val) => handleChange(val, 'firstName')}
            error={
              !formValues.firstName &&
              intl.formatMessage({
                id: 'is.input.field_required',
                defaultMessage: 'This field is mandatory',
              })
            }
            placeholder={intl.formatMessage({
              id: 'is.account_details.first_name_input_placeholder',
              defaultMessage: 'First name',
            })}
            data-test-id="account-first-name-input"
            autoComplete="on"
          />

          <TextField
            label="Last name"
            requiredIndicator={true}
            labelHidden
            name="lastName"
            value={formValues.lastName}
            onChange={(val) => handleChange(val, 'lastName')}
            error={
              !formValues.lastName &&
              intl.formatMessage({
                id: 'is.input.field_required',
                defaultMessage: 'This field is mandatory',
              })
            }
            placeholder={intl.formatMessage({
              id: 'is.account_details.last_name_input_placeholder',
              defaultMessage: 'Last name',
            })}
            data-test-id="account-last-name-input"
            autoComplete="on"
            type="text"
          />

          <TextField
            label="Email address"
            name="email"
            labelHidden
            value={formValues.email}
            disabled
            placeholder={intl.formatMessage({
              id: 'is.account_details.email_input_placeholder',
              defaultMessage: 'Email address',
            })}
            data-test-id="account-email-input"
            type="email"
            autoComplete="off"
          />

          <Box>
            <TextField
              placeholder="****************"
              disabled
              data-test-id="account-password-input"
              autoComplete="off"
              label="password"
              labelHidden
            />

            <Box style={{ marginTop: space['space-16'] }}>
              <Button
                variant="tertiary"
                onClick={nextStep}
                data-test-id="account-change-password-button"
              >
                {intl.formatMessage({
                  id: 'is.account_details.change_password_button',
                  defaultMessage: 'Change password',
                })}
              </Button>
            </Box>
          </Box>

          <TextField
            label="Phone number"
            labelHidden
            name="phone"
            value={formValues.phone}
            onChange={(val) => handleChange(val, 'phone')}
            placeholder={intl.formatMessage({
              id: 'is.account_details.phone_number_input_placeholder',
              defaultMessage: 'Phone number',
            })}
            data-test-id="account-phone-input"
            autoComplete="off"
            type="tel"
          />
        </Box>

        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Text as="p" variant="bodySm">
            <FormattedMessage
              id="is.account_details.mandatory_fields_text"
              defaultMessage="*Mandatory fields"
            />
          </Text>

          <Button
            submit
            disabled={!validate()}
            loading={isLoading}
            data-test-id="account-details-save-button"
          >
            {intl.formatMessage({
              id: 'is.account_details.save_button',
              defaultMessage: 'Save',
            })}
          </Button>
        </Box>
      </AccountPageLayout.Form>
    </AccountPageLayout>
  );
};
